import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Award, FileCheck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";
import axios from "axios";

const API = "/api";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/816767a7-7d1c-4aa6-ab45-83326ee2f608/images/67ff5637ae4cff00de20ed9a9ba1f3e9fbf4407e212ceff27138ac1a7ac8c5d2.png";

const features = [
  {
    icon: Users,
    title: "Un seul interlocuteur",
    description: "Un collaborateur dédié vous accompagne de A à Z dans votre projet."
  },
  {
    icon: Award,
    title: "Estimation précise",
    description: "Étude Comparative de Marché fiable pour une estimation juste."
  },
  {
    icon: Home,
    title: "Visibilité maximale",
    description: "Diffusion sur les principaux portails immobiliers et réseaux sociaux."
  },
  {
    icon: FileCheck,
    title: "Gestion complète",
    description: "Nous gérons toute la partie administrative et juridique."
  }
];

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestProperties = async () => {
      try {
        const response = await axios.get(`${API}/properties/latest?limit=6`);
        setProperties(response.data);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLatestProperties();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative h-[70vh] min-h-[500px] flex items-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B5AD] mb-4 animate-fadeIn">
              Agence Immobilière
            </p>
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl tracking-tight font-light text-white mb-6 animate-fadeIn animate-fadeIn-delay-1">
              Votre projet immobilier
              <span className="block font-medium">entre de bonnes mains</span>
            </h1>
            <p className="text-lg text-zinc-200 mb-8 leading-relaxed animate-fadeIn animate-fadeIn-delay-2">
              De l'estimation à la signature chez le notaire, nous vous accompagnons 
              avec professionnalisme et transparence dans tous vos projets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn animate-fadeIn-delay-3">
              <Link to="/ventes">
                <Button className="bg-[#D62965] hover:bg-[#A61E4E] text-white rounded-none px-8 py-6 text-base font-medium" data-testid="hero-cta-ventes">
                  Voir nos biens
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-zinc-900 rounded-none px-8 py-6 text-base font-medium" data-testid="hero-cta-contact">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B5AD] mb-3">
              Pourquoi nous choisir
            </p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900">
              Notre engagement à vos côtés
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 border border-zinc-200 hover:border-[#D62965] transition-colors group"
                data-testid={`feature-card-${index}`}
              >
                <feature.icon className="h-10 w-10 text-[#00B5AD] mb-4 group-hover:text-[#D62965] transition-colors" />
                <h3 className="font-['Outfit'] font-medium text-lg text-zinc-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Properties Section */}
      <section className="py-20 bg-zinc-50" data-testid="latest-properties-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#00B5AD] mb-3">
                Nos dernières annonces
              </p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900">
                Biens à la vente
              </h2>
            </div>
            <Link to="/ventes">
              <Button variant="outline" className="rounded-none border-zinc-200 hover:border-[#D62965] hover:text-[#D62965]" data-testid="view-all-properties-btn">
                Voir tous les biens
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white border border-zinc-200">
                  <div className="aspect-[4/3] skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 skeleton w-1/3" />
                    <div className="h-6 skeleton w-3/4" />
                    <div className="h-4 skeleton w-1/2" />
                    <div className="h-8 skeleton w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-500">
              <Home className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun bien disponible pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-zinc-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-white mb-6">
            Vous souhaitez vendre votre bien ?
          </h2>
          <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
            Bénéficiez d'une estimation gratuite et sans engagement. 
            Notre équipe vous accompagne dans toutes les étapes de la vente.
          </p>
          <Link to="/contact">
            <Button className="bg-[#D62965] hover:bg-[#A61E4E] text-white rounded-none px-8 py-6 text-base font-medium" data-testid="cta-contact-btn">
              Demander une estimation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
